import { Box, Button, Flex, Tooltip, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { routes } from "../../../../config";
import { H2 } from "components/text/headings";
import {
  indirectEmissionsKeys,
  useAddIndirectEmissionsItem,
  useDefaultOrganizationQuestionnaireConfig,
  useDeleteIndirectEmissionsItem,
  useFinalizeQuestionnaire,
  useGetIndirectEmissionsItems,
  useUpdateIndirectEmissionsItem,
} from "../../../../hooks/query";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { DimensionItemModal, DimensionsGrid, EmissionsDataGrid } from "../DimensionsGrid";
import IndirectEmissionsV2 from "../IndirectEmissionsV2/IndirectEmissionsV2";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { mapEmissionsFormDataToElectricityApiData } from "../../helpers";
import { useGetManualEmissionItems } from "../../../../hooks/query/questionnaire/manual-emission-item";

function IndirectEmissions({
  organizationStructure,
  defaultValues,
  isPreview,
  canFinalize = true,
  onHasData,
  onDownloadTemplate,
  onImport,
  dataType,
  methodologyAndSourcesPanel,
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleSaveAndClose = () => navigate(routes.dataCollectionDataEntryPlanning);

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isDimensionItemModalOpen,
    onOpen: onDimensionItemModalOpen,
    onClose: onDimensionItemModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose,
  } = useDisclosure();

  const { id: questionnaireId, dimensionItems } = defaultValues;

  // ESG-3927: resolve the Scope 2 engine for this questionnaire. Per-questionnaire
  // override wins, else the org-level default, else legacy V1. When V2, the
  // activity-mode entry renders the categorized IndirectEmissionsV2 instead of
  // the v1 electricity grid. The emissions-data (manual tCO2e) path is shared.
  const scopeTwoVersion =
    defaultValues?.scopeTwoVersion ?? organizationStructure?.scopeTwoVersion ?? "V1";
  const isScopeTwoV2 = scopeTwoVersion === "V2";

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32",
  });

  const ELECTRICITY_SECTION = "electricity";

  const [selectedItem, setSelectedItem] = useState(null);
  const [dimensionItemModalData, setDimensionItemModalData] = useState(null);
  const [sectionsSize, setSectionsSize] = useState({});

  const { mutate: finalize } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: addItem } = useAddIndirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: updateItem } = useUpdateIndirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutate: deleteItem } = useDeleteIndirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const { mutateAsync: deleteItemAsync } = useDeleteIndirectEmissionsItem();

  const handleClearAllItems = async (items) => {
    if (!items || items.length === 0) return;
    dispatch(showAppSpinner());
    const results = await Promise.allSettled(
      items.map((item) => deleteItemAsync({ questionnaireId, recordId: item.id, type: ELECTRICITY_SECTION }))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    queryClient.invalidateQueries(indirectEmissionsKeys.items());
    dispatch(hideAppSpinner());
    if (failed === 0) {
      showSuccessToast(toast, `Cleared ${items.length} ${items.length === 1 ? "row" : "rows"}`);
    } else {
      showErrorToast(toast, `Cleared ${items.length - failed} ${items.length - failed === 1 ? "row" : "rows"}, ${failed} failed`);
    }
  };

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleAddItemClick = () => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "two",
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
    });
    onDimensionItemModalOpen();
  };

  const handleEditItemClick = (item) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "two",
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
      editData: item,
    });
    onDimensionItemModalOpen();
  };

  const handleDeleteItemClick = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: ELECTRICITY_SECTION });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleDimensionItemModalSubmit = (data) => {
    const apiData = mapEmissionsFormDataToElectricityApiData(data[ELECTRICITY_SECTION]);
    dispatch(showAppSpinner());
    if (dimensionItemModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: ELECTRICITY_SECTION,
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: ELECTRICITY_SECTION,
      });
    }
  };

  const handleDimensionItemModalClose = () => {
    setDimensionItemModalData(null);
    onDimensionItemModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size,
    });
  };

  const { data: manualEmissionItems = [] } = useGetManualEmissionItems(
    { questionnaireId, scopeCategoryKey: "SCOPE_2" },
    { enabled: isPreview && !!questionnaireId }
  );
  const hasManualEmissions = manualEmissionItems.length > 0;

  const noActivityData = sectionsSize && Object.keys(sectionsSize).length === 1 &&
    typeof Object.keys(sectionsSize).find((key) => sectionsSize[key] > 0) === "undefined";
  const noData = isPreview && noActivityData && !hasManualEmissions;

  useEffect(() => {
    if (onHasData && isPreview) onHasData(!noData);
  }, [noData, onHasData, isPreview]);

  return (
    <Fragment>
      {noData ? (
        <Box marginBlockStart="10" opacity={0.4} pointerEvents="none">
          <H2>{t("noDataAvailable")}</H2>
        </Box>
      ) : (
        <Box marginBlockStart="10" overflow="auto">
          {questionnaireId && organizationStructure && (
            <>
              {isPreview || dataType !== "emissions" ? (
                isScopeTwoV2 ? (
                  <IndirectEmissionsV2
                    organizationStructure={organizationStructure}
                    defaultValues={defaultValues}
                    isPreview={isPreview}
                    dataType={dataType}
                    // The page renders methodologyAndSourcesPanel after this
                    // block for both engines — don't duplicate it here.
                    methodologyAndSourcesPanel={null}
                  />
                ) : (
                  <DimensionsGrid
                    questionnaireId={questionnaireId}
                    scope="two"
                    organizationStructure={organizationStructure}
                    sectionType={ELECTRICITY_SECTION}
                    consumptionTypesAttr={"electricity"}
                    getItemsFunction={useGetIndirectEmissionsItems}
                    handleAddItem={() => handleAddItemClick()}
                    handleEditItem={(dataItem) => handleEditItemClick(dataItem)}
                    handleDeleteItem={(dataItem) => handleDeleteItemClick(dataItem)}
                    handleClearAll={(items) => handleClearAllItems(items)}
                    isPreview={isPreview}
                    onDataItemsChange={handleSectionSizeChange}
                    onDownloadTemplate={onDownloadTemplate}
                    importBtnId="importScope2ElectricityBtn"
                    onImport={onImport}
                  />
                )
              ) : null}
              {isPreview || dataType === "emissions" ? (
                <EmissionsDataGrid
                  organizationStructure={organizationStructure}
                  dimensionItems={dimensionItems}
                  isPreview={isPreview}
                  questionnaireId={questionnaireId}
                  scopeCategoryKey="SCOPE_2"
                />
              ) : null}
            </>
          )}
        </Box>
      )}
      {methodologyAndSourcesPanel}
      {!isPreview && (
        <Flex
          data-sticky-footer
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={4}
          zIndex="10"
        >
          <Button
            flex={1}
            bg="limeShade.100"
            color="pureBlack"
            _hover={{ bg: "limeShade.200" }}
            onClick={handleSaveAndClose}
          >
            {t("buttons.saveAndClose")}
          </Button>
          <Tooltip label={canFinalize ? "" : "Add data, methodology, or a file before finalising"} placement="top" hasArrow>
            <Button
              flex={1}
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              isDisabled={!canFinalize}
              onClick={() => {
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Tooltip>
        </Flex>
      )}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {isDimensionItemModalOpen && dimensionItemModalData !== null && (
        <DimensionItemModal
          modalData={dimensionItemModalData}
          isOpen={isDimensionItemModalOpen}
          onClose={handleDimensionItemModalClose}
          onSubmit={handleDimensionItemModalSubmit}
          scope="two"
        />
      )}
    </Fragment>
  );
}

export default IndirectEmissions;
